import api from "config/axiosConfig";
import { toast } from "sonner";
import { CoauthorSuggestion } from "../components/Coauthors/CoauthorSelect";
import { getPendingCoauthorRequests } from "./getObjectDetails";

/**
 * Interface for entity with common properties needed for coauthor management
 */
export interface EntityWithCoauthors {
  id: number;
  user_id: number;
  properties?: {
    coauthors?: any[];
    [key: string]: any;
  };
}

/**
 * Interface for state setters used in coauthor management
 */
export interface CoauthorStateSetters {
  setExistingCoauthors: React.Dispatch<React.SetStateAction<CoauthorSuggestion[]>>;
  setNewCoauthors?: React.Dispatch<React.SetStateAction<CoauthorSuggestion[]>>;
  setRemovedCoauthors?: React.Dispatch<React.SetStateAction<CoauthorSuggestion[]>>;
  setPendingCoauthors: React.Dispatch<React.SetStateAction<CoauthorSuggestion[]>>;
  setIsLoadingCoauthors: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * Loads coauthor data for an entity, including existing and pending coauthors
 * 
 * @param entity - The entity to load coauthors for (impact, solution, etc.)
 * @param entityType - The type of entity (impact, solution, etc.)
 * @param currentUserId - The ID of the current user
 * @param stateSetters - Object containing state setter functions
 */
export const loadCoauthorData = async (
  entity: EntityWithCoauthors,
  entityType: string,
  currentUserId: number | undefined,
  stateSetters: CoauthorStateSetters
) => {
  const { setExistingCoauthors, setPendingCoauthors, setIsLoadingCoauthors } = stateSetters;
  
  setIsLoadingCoauthors(true);
  
  try {
    // First, extract existing coauthors from the entity properties
    const coauthorsArray = entity.properties?.coauthors || [];
    const coauthorPromises = coauthorsArray.map(async (coauthor: any) => {
      const type = coauthor.type || "user";
      const actorData = await (type === "user"
        ? api.get(`/api/user/id/${coauthor.id}`)
        : api.get(`/api/team/id/${coauthor.id}`));
      
      return {
        id: coauthor.id,
        type,
        name: type === "user" ? actorData.data.username : actorData.data.teamname,
        display_name: actorData.data.display_name,
      } as CoauthorSuggestion;
    });
    
    const coauthorData = await Promise.all(coauthorPromises);
    setExistingCoauthors(coauthorData);
    
    // Now, load pending coauthor requests if the current user is the owner
    if (currentUserId && currentUserId === entity.user_id) {
      const pendingRequests = await getPendingCoauthorRequests(entityType, entity.id);
      setPendingCoauthors(pendingRequests);
    }
  } catch (error) {
    console.error("Error loading coauthor data:", error);
  } finally {
    setIsLoadingCoauthors(false);
  }
};

/**
 * Handles adding a new coauthor
 * 
 * @param suggestion - The coauthor suggestion to add
 * @param existingCoauthors - Array of existing coauthors
 * @param newCoauthors - Array of newly added coauthors
 * @param setNewCoauthors - State setter for newCoauthors
 * @param onCoauthorsChange - Optional callback when coauthors change
 * @param removedCoauthors - Array of removed coauthors
 * @returns Boolean indicating if the coauthor was added
 */
export const handleCoauthorSelect = (
  suggestion: CoauthorSuggestion,
  existingCoauthors: CoauthorSuggestion[],
  newCoauthors: CoauthorSuggestion[],
  setNewCoauthors: React.Dispatch<React.SetStateAction<CoauthorSuggestion[]>>,
  onCoauthorsChange?: (added: CoauthorSuggestion[], removed: CoauthorSuggestion[]) => void,
  removedCoauthors: CoauthorSuggestion[] = []
): boolean => {
  const isExisting = existingCoauthors.some(
    c => parseInt(c.id as any) === parseInt(suggestion.id as any) && c.type === suggestion.type
  );
  
  const isNew = newCoauthors.some(
    c => parseInt(c.id as any) === parseInt(suggestion.id as any) && c.type === suggestion.type
  );

  if (!isExisting && !isNew) {
    const updatedNewCoauthors = [...newCoauthors, suggestion];
    setNewCoauthors(updatedNewCoauthors);
    onCoauthorsChange?.(updatedNewCoauthors, removedCoauthors);
    return true;
  }
  
  return false;
};

/**
 * Handles removing a coauthor or cancelling a pending coauthor request
 * 
 * @param coauthor - The coauthor to remove
 * @param stateSetters - Object containing state setter functions
 * @param stateArrays - Object containing state arrays
 * @param onCoauthorsChange - Optional callback when coauthors change
 */
export const handleCoauthorRemove = async (
  coauthor: CoauthorSuggestion,
  stateSetters: Pick<CoauthorStateSetters, "setNewCoauthors" | "setRemovedCoauthors" | "setPendingCoauthors" | "setExistingCoauthors">,
  stateArrays: {
    newCoauthors: CoauthorSuggestion[];
    removedCoauthors: CoauthorSuggestion[];
  },
  onCoauthorsChange?: (added: CoauthorSuggestion[], removed: CoauthorSuggestion[]) => void
) => {
  const { setNewCoauthors, setRemovedCoauthors, setPendingCoauthors, setExistingCoauthors } = stateSetters;
  const { newCoauthors, removedCoauthors } = stateArrays;
  
  // If it's a pending coauthor, cancel the request.
  if (coauthor.isPending && coauthor.request_id) {
    //console.log('cancelling coauthor invitation', coauthor);
    try {
      await api.patch(`/api/requests/${coauthor.request_id}`, { status: "cancelled" });
      setPendingCoauthors(prev =>
        prev.filter(pending => pending.request_id !== coauthor.request_id)
      );
      toast.success(`Invitation to ${coauthor.name} was cancelled`);
    } catch (error) {
      console.error("Error cancelling coauthor invitation:", error);
      toast.error("Failed to cancel invitation");
    }
    return;
  }
  
  // If the coauthor is new, remove it from newCoauthors and ensure it is not added to removedCoauthors.
  if (newCoauthors.some(c => parseInt(c.id as any) === parseInt(coauthor.id as any) && c.type === coauthor.type)) {
    //console.log('removing new coauthor', coauthor);
    const updatedNewCoauthors = newCoauthors.filter(
      c => !(parseInt(c.id as any) === parseInt(coauthor.id as any) && c.type === coauthor.type)
    );
    setNewCoauthors?.(updatedNewCoauthors);
    // Also, clear it from removedCoauthors if it exists.
    const updatedRemovedCoauthors = removedCoauthors.filter(
      c => !(parseInt(c.id as any) === parseInt(coauthor.id as any) && c.type === coauthor.type)
    );
    setRemovedCoauthors?.(updatedRemovedCoauthors);
    if (onCoauthorsChange) {
      onCoauthorsChange(updatedNewCoauthors, updatedRemovedCoauthors);
    }
  } else {
    //console.log('removing existing coauthor', coauthor);
    // Remove from existing coauthors and add to removedCoauthors if not already present.
    setExistingCoauthors(prev =>
      prev.filter(c => !(parseInt(c.id as any) === parseInt(coauthor.id as any) && c.type === coauthor.type))
    );
    if (!removedCoauthors.some(c => parseInt(c.id as any) === parseInt(coauthor.id as any) && c.type === coauthor.type)) {
      //console.log('adding removed coauthor', coauthor);
      const updatedRemovedCoauthors = [...removedCoauthors, coauthor];
      setRemovedCoauthors?.(updatedRemovedCoauthors);
      if (onCoauthorsChange) {
        onCoauthorsChange(newCoauthors, updatedRemovedCoauthors);
      }
    } else {
      if (onCoauthorsChange) {
        onCoauthorsChange(newCoauthors, removedCoauthors);
      }
    }
  }
};
